import { useRef } from 'react';
import PeriodPicker from './PeriodPicker';
import { PRESETS_ANALYTIC } from '../utils/period';
import { useIsMobile } from '../utils/useIsMobile';
import { useStickyFilterOffset } from '../utils/useStickyFilterOffset';
import Icon from './Icon';

// Filter bar scoped to /credits and /credits/history. Follows
// FILTER_AND_DATE_RULES.md spec — same shape as Suppliers/Documents/Analysis:
// search on the start side, date range anchored to the far end. Active
// filters render as chips below (date excluded).
//
// There is NO status filter here — status is now the ROUTE (CreditsViewTabs:
// פתוחים vs היסטוריית זיכויים), mirroring SuppliersFilterBar having no
// payment-status filter for the same reason (PaymentViewTabs replaced it):
// a filter promises fewer rows and can't change what the page's cards mean,
// which is exactly what made the old status multi-select here misleading —
// its cards silently disagreed with whatever the filter was hiding.
//
// ≤720px the bar collapses to search only (+ the mobile sort control, when
// given one) — with status gone there is nothing left to put in a filter
// sheet, so unlike SuppliersFilterBar this page has no FilterButton/sheet at
// all rather than shipping a trigger that opens an empty one.
export default function CreditsFilterBar({
  nameFilter,    setNameFilter,
  dateFrom,      dateTo,
  setDateRange,
  onClearAll,
  // Mobile-only sort pill (a MobileSort). The list rows have no column headers,
  // so this is the mobile counterpart of the sortable desktop <th>s.
  sortControl,
  // Optional CreditsMetricsRow node — mobile only. (A `viewTabs` slot used to
  // live here too, for the tabs; both credits pages now dock their tabs inside
  // the data card at every breakpoint, same as /suppliers, so the slot had no
  // caller left. SuppliersFilterBar dropped its own for the same reason.)
  // Placed between the date bar and the tabs so reading order is
  // "when → how much (total picture) → which slice (tab) → the rows
  // themselves" — the date picker leads because it's the one control that
  // silently reframes everything below it, and the metrics sit right after
  // it (not buried under the tabs) because they answer the same question
  // regardless of which tab is active.
  metricsRow,
}) {
  const isMobile = useIsMobile();
  const hasChips = Boolean(nameFilter);
  const wrapRef = useRef(null);
  useStickyFilterOffset(wrapRef);

  const searchPill = (
    <label className={`docs-filter-control docs-filter-searchpill${nameFilter ? ' is-active' : ''}`}>
      <Icon name="search" size={14} className="docs-filter-microlabel" />
      <input
        className="docs-filter-select"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        placeholder="חפש לפי שם ספק"
        style={{ minWidth: isMobile ? 0 : 160 }}
      />
    </label>
  );

  // One date control for both months and arbitrary day ranges. `compact` is the
  // mobile-row variant — same component and state, just no microlabel.
  const dateRange = (variant) => (
    <PeriodPicker
      dateFrom={dateFrom}
      dateTo={dateTo}
      onChange={(f, t) => setDateRange(f, t)}
      presets={PRESETS_ANALYTIC}
      variant={variant}
    />
  );

  const renderChips = () => (
    <div className="docs-filter-chips">
      {nameFilter && (
        <span className="docs-filter-chip">
          חיפוש: {nameFilter}
          <button
            type="button"
            aria-label="נקה חיפוש"
            onClick={() => setNameFilter('')}
            className="docs-filter-chip-x"
          ><Icon name="close" size={11} strokeWidth={2} /></button>
        </span>
      )}
      <button type="button" className="docs-filter-clearall" onClick={onClearAll}>
        נקה הכל
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <>
      <div className="period-bar" ref={wrapRef}>{dateRange('bar')}</div>
      {metricsRow}
      <div className="docs-filter-wrap">
        <div className="docs-filter-bar docs-filter-bar--mobile">
          {searchPill}
          {sortControl}
        </div>
        {hasChips && renderChips()}
      </div>
      </>
    );
  }

  return (
    <div className="docs-filter-wrap" ref={wrapRef}>
      <div className="docs-filter-bar">
        {searchPill}
        {dateRange('pill')}
      </div>

      {hasChips && renderChips()}
    </div>
  );
}
